import type { EventoHistoricoAtivo, MotivoExclusaoAtivo, StatusCustodiaEquipamento } from '../../../../models';
import {
  MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS,
  SISTEMA_OPERACIONAL_LABELS,
} from '../../../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import { TIPO_SOLICITACAO_LABELS } from '../../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { PROCESSO_MOVIMENTACAO_LABELS } from '../../equipamentosListPage.definitions';
import {
  DETALHES_SITUACAO_ATIVO,
  PROPRIEDADE_ATIVO_LABELS,
  SITUACAO_ATIVO_LABELS,
  SITUACAO_POR_STATUS,
  TIPO_ATIVO_LABELS,
} from './ativos.definitions';
import { MOTIVO_EXCLUSAO_ATIVO_LABELS, type ContextoAtivos } from './ativos.utils';

/**
 * Data de um evento/campo em qualquer formato que existe no banco — Timestamp do Firestore,
 * mas também texto/número/Date em dados antigos (o resto do SI já trata isso, ver
 * GerarTermoButton.paraDate). Nunca lança: formato ilegível → null (a tela mostra sem data
 * em vez de quebrar).
 */
export function paraDataSegura(valor: unknown): Date | null {
  if (valor == null || valor === '') return null;
  let data: Date | null = null;
  if (valor instanceof Date) data = valor;
  else if (typeof valor === 'object' && 'toDate' in valor && typeof (valor as { toDate: unknown }).toDate === 'function') {
    data = (valor as { toDate: () => Date }).toDate();
  } else if (typeof valor === 'object' && ('seconds' in valor || '_seconds' in valor)) {
    const s = (valor as { seconds?: number; _seconds?: number }).seconds ?? (valor as { _seconds?: number })._seconds;
    data = typeof s === 'number' ? new Date(s * 1000) : null;
  } else if (typeof valor === 'string' || typeof valor === 'number') data = new Date(valor);
  return data && !Number.isNaN(data.getTime()) ? data : null;
}

export function formatarDataHora(valor: unknown): string {
  const data = paraDataSegura(valor);
  if (!data) return 'data não registrada';
  return `${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function formatarData(valor: unknown): string {
  return paraDataSegura(valor)?.toLocaleDateString('pt-BR') ?? '';
}

/** Rótulo de cada campo rastreado (mesma lista de CAMPOS_RASTREADOS no servidor). */
export const ROTULO_CAMPO_ATIVO: Record<string, string> = {
  patrimonio: 'Patrimônio',
  tipo: 'Tipo',
  nSerie: 'Nº de série',
  modelo: 'Modelo',
  fabricante: 'Fabricante',
  sistemaOperacional: 'Sistema operacional',
  processador: 'Processador',
  memoria: 'Memória RAM',
  tamanhoDisco: 'Armazenamento',
  colaboradorAtualId: 'Colaborador',
  clienteAtualId: 'Cliente atual',
  propriedade: 'Propriedade',
  clienteProprietarioId: 'Cliente proprietário',
  projetoClienteId: 'Projeto',
  dataCompra: 'Data de compra',
  notaFiscalNumero: 'Nº da nota fiscal',
  fornecedor: 'Fornecedor',
  fornecedorManutencao: 'Fornecedor da manutenção',
  valor: 'Valor',
  linha: 'Linha',
  operadora: 'Operadora',
  msin: 'MSIN',
  pin1: 'PIN 1',
  puk1: 'PUK 1',
  pin2: 'PIN 2',
  puk2: 'PUK 2',
  numeroCartao: 'Número do cartão',
};

/** PIN/PUK não aparecem no histórico — só que foram alterados. */
const CAMPOS_SIGILOSOS = new Set(['pin1', 'puk1', 'pin2', 'puk2']);

function formatarValor(campo: string, valor: unknown, contexto: ContextoAtivos): string {
  if (valor == null || valor === '') return '—';
  if (typeof valor === 'object' || campo === 'dataCompra') {
    return formatarData(valor) || '—';
  }
  const texto = String(valor);
  switch (campo) {
    case 'colaboradorAtualId':
      return contexto.nomeColaboradorPorId.get(texto) ?? 'Colaborador não encontrado';
    case 'clienteAtualId':
    case 'clienteProprietarioId':
    case 'projetoClienteId':
      return contexto.nomeClientePorId.get(texto) ?? 'Cliente não encontrado';
    case 'tipo':
      return TIPO_ATIVO_LABELS[texto] ?? texto;
    case 'propriedade':
      return PROPRIEDADE_ATIVO_LABELS[texto as keyof typeof PROPRIEDADE_ATIVO_LABELS] ?? texto;
    case 'sistemaOperacional':
      return SISTEMA_OPERACIONAL_LABELS[texto as keyof typeof SISTEMA_OPERACIONAL_LABELS] ?? texto;
    case 'valor':
      return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    default:
      return texto;
  }
}

export function descreverSituacao(status: StatusCustodiaEquipamento | null, detalhe: string | null): string {
  if (!status) return '—';
  const situacao = SITUACAO_POR_STATUS[status];
  const rotulo = situacao ? SITUACAO_ATIVO_LABELS[situacao] : status;
  const rotuloDetalhe = detalhe && situacao ? DETALHES_SITUACAO_ATIVO[situacao]?.[detalhe] : null;
  return rotuloDetalhe ? `${rotulo} · ${rotuloDetalhe}` : rotulo;
}

export interface EventoDescrito {
  titulo: string;
  linhas: string[];
  /** "por Fulano", "automático — pela Solicitação", etc. */
  origem: string;
  /** Link da Solicitação de origem, quando houver. */
  solicitacaoId: string | null;
}

export function descreverOrigem(evento: EventoHistoricoAtivo): string {
  const { origem } = evento;
  switch (origem?.tipo) {
    case 'manual':
      return origem.ator?.name ? `por ${origem.ator.name}` : 'manual';
    case 'movimentacao':
      return origem.termoUrl ? 'automático — Movimentação entregue (com termo)' : 'automático — Movimentação entregue';
    case 'estadia':
      return 'automático — Estadia em Cliente';
    case 'recuperado':
      return 'recuperado das Solicitações antigas';
    case 'importacao_glpi':
      return origem.ator?.name ? `importado do GLPI por ${origem.ator.name}` : 'importado do GLPI';
    default:
      return 'origem não registrada';
  }
}

const MOTIVO_ENCERRAMENTO_ESTADIA_LABELS: Record<string, string> = {
  enviado_ao_colaborador: 'enviada a um colaborador',
  retornado_para_raro: 'recolhida pra Raro',
  cancelado: 'cancelada',
};

/** Texto de um evento recuperado das Solicitações/Estadias antigas. */
export function descreverRecuperado(evento: EventoHistoricoAtivo, contexto: ContextoAtivos): { titulo: string; linhas: string[] } {
  const r = evento.recuperado;
  if (!r) return { titulo: 'Evento anterior', linhas: [] };
  const colaborador = r.colaboradorNome || 'colaborador não informado';
  const cliente = r.clienteId ? (contexto.nomeClientePorId.get(r.clienteId) ?? 'Cliente não encontrado') : null;
  const envio = r.tipoFluxo === 'envio';
  const tipoSolicitacao = r.tipoSolicitacao ? TIPO_SOLICITACAO_LABELS[r.tipoSolicitacao as keyof typeof TIPO_SOLICITACAO_LABELS] : null;
  const linhas = [tipoSolicitacao ? `Solicitação: ${tipoSolicitacao}${cliente ? ` — ${cliente}` : ''}` : null].filter(
    (l): l is string => Boolean(l)
  );
  switch (r.etapa) {
    case 'vinculado':
      return { titulo: envio ? `Vinculado a um envio para ${colaborador}` : `Vinculado a uma devolução de ${colaborador}`, linhas };
    case 'enviado':
      return { titulo: envio ? `Enviado para ${colaborador}` : `Postado de volta por ${colaborador}`, linhas };
    case 'entregue':
      if (envio) return { titulo: `Entregue a ${colaborador}`, linhas };
      return {
        titulo: r.destinoAposDevolucao === 'direto_ao_cliente' ? `Devolvido ao Cliente${cliente ? ` ${cliente}` : ''}` : 'Recebido na Raro',
        linhas,
      };
    case 'movimentacao_excluida':
      return {
        titulo: 'Movimentação excluída',
        linhas: [
          ...linhas,
          ...(r.motivo ? [`Motivo: ${MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS[r.motivo as keyof typeof MOTIVO_EXCLUSAO_MOVIMENTACAO_LABELS] ?? r.motivo}`] : []),
        ],
      };
    case 'estadia_enviada':
      return {
        titulo: `Enviado para o Cliente${cliente ? ` ${cliente}` : ''}`,
        linhas: r.motivo ? [`Motivo: ${PROCESSO_MOVIMENTACAO_LABELS[r.motivo as keyof typeof PROCESSO_MOVIMENTACAO_LABELS] ?? r.motivo}`] : [],
      };
    case 'estadia_chegou':
      return { titulo: `Chegou no Cliente${cliente ? ` ${cliente}` : ''}`, linhas: [] };
    case 'estadia_encerrada':
      return {
        titulo: `Estadia no Cliente encerrada${r.motivo ? ` — ${MOTIVO_ENCERRAMENTO_ESTADIA_LABELS[r.motivo] ?? r.motivo}` : ''}`,
        linhas: [],
      };
    default:
      return { titulo: 'Evento anterior', linhas };
  }
}

/** Evento → texto da linha do tempo (função pura). */
export function descreverEvento(evento: EventoHistoricoAtivo, contexto: ContextoAtivos): EventoDescrito {
  const base = { origem: descreverOrigem(evento), solicitacaoId: evento.origem?.solicitacaoId ?? null };
  switch (evento.tipo) {
    case 'criado':
      return { ...base, titulo: 'Cadastro criado', linhas: [] };
    case 'situacao': {
      const s = evento.situacao;
      return {
        ...base,
        titulo: 'Situação alterada',
        linhas: [
          `${descreverSituacao(s?.antes ?? null, s?.detalheAntes ?? null)} → ${descreverSituacao(s?.depois ?? null, s?.detalheDepois ?? null)}`,
          ...(s?.motivo ? [`Motivo: ${s.motivo}`] : []),
        ],
      };
    }
    case 'campos':
      return {
        ...base,
        titulo: 'Dados alterados',
        linhas: (evento.mudancas ?? []).map((m) => {
          const rotulo = ROTULO_CAMPO_ATIVO[m.campo] ?? m.campo;
          if (CAMPOS_SIGILOSOS.has(m.campo)) return `${rotulo}: alterado`;
          return `${rotulo}: ${formatarValor(m.campo, m.antes, contexto)} → ${formatarValor(m.campo, m.depois, contexto)}`;
        }),
      };
    case 'comentario':
      return { ...base, titulo: 'Comentário', linhas: [evento.comentario ?? ''] };
    case 'anexo':
      return {
        ...base,
        titulo: evento.anexo?.acao === 'removido' ? 'Anexo removido da ficha' : 'Arquivo anexado',
        linhas: evento.anexo ? [evento.anexo.nome] : [],
      };
    case 'recuperado':
      return { ...base, ...descreverRecuperado(evento, contexto) };
    case 'excluido': {
      const tipo = evento.exclusao?.motivoTipo as MotivoExclusaoAtivo | null | undefined;
      const motivo = [tipo ? MOTIVO_EXCLUSAO_ATIVO_LABELS[tipo] ?? tipo : null, evento.exclusao?.motivo].filter(Boolean).join(' — ');
      return { ...base, titulo: 'Ativo excluído', linhas: motivo ? [`Motivo: ${motivo}`] : [] };
    }
    case 'restaurado':
      return { ...base, titulo: 'Ativo restaurado', linhas: [] };
    default:
      return { ...base, titulo: 'Evento', linhas: [] };
  }
}
